import { useState } from "react";
import { goToCheckout } from "@/components/CheckoutPage";
import { Check, Loader2, Mail, Minus, Plus, Printer, Send, Users } from "lucide-react";
import { api } from "@/App";

const MINT = "#4ff2c2";
const PACK = 169;
const MAX_SEATS = 50;
export const seatDiscount = (n) => (n >= 5 ? 20 : n >= 2 ? 10 : 0);
const eur = (v) => `€${Number.isInteger(v) ? v : v.toFixed(2)}`;
const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong, please try again.");

const SeatPicker = ({ seats, setSeats, testid }) => {
  const [draft, setDraft] = useState(null);
  const commit = (v) => { setSeats(Math.min(MAX_SEATS, Math.max(1, Math.round(Number(v) || 1)))); setDraft(null); };
  return (
  <div className="flex items-center border border-[#2a2f3d]" data-testid={`${testid}-seats`}>
    <button onClick={() => setSeats(Math.max(1, seats - 1))} className="px-2 py-2 text-[#9ba3b4] hover:text-white transition-colors" aria-label="fewer seats" data-testid={`${testid}-seats-minus`}><Minus size={12} /></button>
    <input type="text" inputMode="numeric" value={draft ?? seats}
      onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 2); setDraft(v); if (v) setSeats(Math.min(MAX_SEATS, Math.max(1, Number(v)))); }}
      onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && commit(e.currentTarget.value)}
      className="w-12 bg-transparent text-center text-sm text-white outline-none" aria-label="seats" data-testid={`${testid}-seats-input`} />
    <button onClick={() => setSeats(Math.min(MAX_SEATS, seats + 1))} className="px-2 py-2 text-[#9ba3b4] hover:text-white transition-colors" aria-label="more seats" data-testid={`${testid}-seats-plus`}><Plus size={12} /></button>
  </div>
  );
};

export const BuyForTeam = ({ testid = "buy-team-btn" }) => {
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const d = seatDiscount(seats), unit = (PACK * (100 - d)) / 100, total = Math.round(unit * seats * 100) / 100;
  const buy = async () => {
    setBusy(true); setErr(null);
    try {
      goToCheckout({ lookup_key: "ai_pack", quantity: seats });
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-1 no-print">
      <div className="flex items-stretch gap-2 flex-wrap">
        <SeatPicker seats={seats} setSeats={setSeats} testid={testid} />
        <button onClick={buy} disabled={busy} className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase text-black transition-opacity hover:opacity-85 disabled:opacity-60" style={{ background: MINT }} data-testid={testid}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Buy for my team · {eur(total)}
        </button>
      </div>
      <span className="text-[11px] text-[#8b93a7]" data-testid={`${testid}-summary`}>
        {seats} {seats === 1 ? "seat" : "seats"} × AI Pack {eur(unit)}{d ? ` (${d}% team discount)` : " · 10% off from 2 seats, 20% off from 5"}
      </span>
      {err && <span className="text-xs text-red-400" data-testid={`${testid}-error`}>{err}</span>}
    </div>
  );
};

const field = "bg-[#101214] border border-[#2a2f3d] px-3 py-2 text-sm text-white focus:border-white outline-none transition-colors w-full";

const EmailQuote = ({ quote }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to: "", name: "", note: "" });
  const [state, setState] = useState({ busy: false, sent: false, err: null });
  const edit = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (state.err) setState({ ...state, err: null }); };
  const send = async (e) => {
    e.preventDefault();
    setState({ busy: true, sent: false, err: null });
    try {
      await api.post("/quote/email", { ...f, ...quote });
      setState({ busy: false, sent: true, err: null });
    } catch (x) { setState({ busy: false, sent: false, err: errMsg(x) }); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} className="no-print flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase border transition-colors hover:bg-white hover:text-black" style={{ borderColor: "#f2f4f8", color: "#f2f4f8" }} data-testid="quote-email-open-btn">
      <Mail size={14} /> Email to my producer
    </button>
  );
  if (state.sent) return <p className="no-print w-full text-sm flex items-center gap-2" style={{ color: MINT }} data-testid="quote-email-sent"><Check size={14} /> Quote sent to {f.to}.</p>;
  return (
    <form onSubmit={send} className="no-print w-full grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="quote-email-form">
      <input type="email" required placeholder="Producer's email" value={f.to} onChange={edit("to")} className={field} data-testid="quote-email-to" />
      <input placeholder="Your name (optional)" maxLength={80} value={f.name} onChange={edit("name")} className={field} data-testid="quote-email-name" />
      <textarea placeholder="Note (optional)" maxLength={500} rows={2} value={f.note} onChange={edit("note")} className={`${field} sm:col-span-2`} data-testid="quote-email-note" />
      <div className="sm:col-span-2 flex items-center gap-3">
        <button type="submit" disabled={state.busy} className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase text-black transition-opacity hover:opacity-85 disabled:opacity-60" style={{ background: MINT }} data-testid="quote-email-send-btn">
          {state.busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send quote
        </button>
        {state.err && <span className="text-xs text-red-400" data-testid="quote-email-error">{state.err}</span>}
      </div>
    </form>
  );
};

const Row = ({ k, v, id }) => (
  <div className="flex justify-between gap-6 py-2 border-b border-[#2a2f3d] quote-row">
    <span className="text-xs font-mono uppercase text-[#9ba3b4] quote-muted">{k}</span>
    <span className="text-sm text-white quote-ink" data-testid={`quote-${id}`}>{v}</span>
  </div>
);

export const QuoteCard = ({ tool, label, sec, fps, pct, cost, showFrames }) => {
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="quote-print border bg-[#0e1013] p-6 max-w-[720px]" style={{ borderColor: MINT }} data-testid="quote-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-xs font-mono uppercase quote-muted" style={{ color: MINT }}>Shared quote · KolorKlinic AI</span>
          <h3 className="font-display uppercase text-2xl mt-1 quote-ink">fal.ai cost estimate</h3>
        </div>
        <span className="text-xs text-[#8b93a7] quote-muted" data-testid="quote-date">{date}</span>
      </div>
      <div className="mt-4">
        <Row k="Tool" v={label} id="tool" />
        <Row k="Shot length" v={`${sec} s`} id="seconds" />
        {showFrames && <Row k="Frame rate" v={`${fps} fps`} id="fps" />}
        {showFrames && <Row k="Frames changed" v={`${pct}%`} id="pct" />}
        <Row k="Plugin licence" v="€99 one-time per plugin · AI Pack (both) €169" id="licence" />
      </div>
      <div className="mt-5">
        <span className="block text-xs font-mono uppercase text-[#9ba3b4] quote-muted">Estimated fal.ai cost</span>
        <span className="font-display text-4xl quote-ink" style={{ color: MINT }} data-testid="quote-cost">{cost}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-start gap-2">
        <button onClick={() => window.print()} className="no-print flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase border transition-colors hover:bg-white hover:text-black" style={{ borderColor: "#f2f4f8", color: "#f2f4f8" }} data-testid="quote-print-btn">
          <Printer size={14} /> Print / Save PDF
        </button>
        <EmailQuote quote={{ tool, s: sec, fps, pct }} />
      </div>
      <div className="mt-4"><BuyForTeam testid="quote-buy-team-btn" /></div>
      <p className="mt-4 text-xs text-[#5c6479] quote-muted">Billed by fal.ai to your own account, no markup. fal.ai sets the prices and they can change. kolorklinic.com/ai</p>
    </div>
  );
};
